import React from 'react';
import { action, computed, observable, makeAutoObservable } from 'mobx';
import { MobxMapAutoNew } from 'src_common/common/mobx-utils/MobxMapAutoNew';
import { Resource } from 'src_common/common/mobx-utils/Resource';
import { OpenapiProxyStaffGetMarketTemplatesResponse200Type } from 'src/api_openapi/generated/openapi_proxy_staff_get_market_templates';
import { Amount } from 'src_common/common/amount/Amount';
import { OpenapiProxyStaffUpdateSportGradesParamsType } from 'src/api_openapi/generated/openapi_proxy_staff_update_sport_grades';
import { Value } from 'src_common/common/mobx-utils/Value';
import { timeout } from 'src_common/common/mobx-utils/timeout';

const getCurrentSeconds = (): number => Math.floor(new Date().getTime() / 1000);

const currentTime = new Value<null | number>(null, (setValue) => {
    setValue(getCurrentSeconds());

    const timer = setInterval(() => {
        setValue(getCurrentSeconds());
    }, 1000);

    return (): void => {
        clearInterval(timer);
    };
});

type MarketLimitsUpdateType = OpenapiProxyStaffUpdateSportGradesParamsType['requestBody']['marketLimits'][string];

type MarketTemplateType = OpenapiProxyStaffGetMarketTemplatesResponse200Type['results'][number];

interface DefaultLimitsType {
    liabilityLimit: string;
    maxBet: string;
    maxMultipleBet: string;
    maxWin: string;
}

export type GradesCustomType = {
    displayOrder: number;
    id: number;
    name: string;
    displayName?: null | undefined | string;
    url: string;
    default: boolean;
    defaultLimits: DefaultLimitsType;
    marketLimits: Record<string, DefaultLimitsType>;
};

type GradeType = GradesCustomType;

export type LimitFieldNameType = 'maxBet' | 'maxWin' | 'liabilityLimit' | 'maxMultipleBet';

class DefaultLimitsInputState {
    @observable private value: string | null = null;
    @observable private lastSave: number | null = null;

    public constructor(
        private getGrade: (gradeId: number) => GradeType | null,
        private readonly gradeId: number,
        private readonly templateId: string,
        private readonly fieldId: LimitFieldNameType
    ) {
        makeAutoObservable(this);
    }

    public setValue(value: string): void {
        if (value === '') {
            this.value = value;
            return;
        }

        const valueNumber = parseInt(value, 10);
        if (isNaN(valueNumber) || valueNumber < 0) {
            console.error('setValue, ignore value ->  positive integer was expected, received', value);
            return;
        }

        this.value = value;
    }

    public setLastSave(): void {
        this.lastSave = getCurrentSeconds();
    }

    private getValueOriginal(): string {
        const grade = this.getGrade(this.gradeId);

        if (grade === null) {
            return '0';
        }

        const betLimits = grade.marketLimits[this.templateId];

        if (betLimits === undefined) {
            return grade.defaultLimits[this.fieldId];
        }

        return betLimits[this.fieldId];
    }

    public getValue(): string {
        if (this.value !== null) {
            return this.value;
        }

        return this.getValueOriginal();
    }

    public get isError(): boolean {
        const lastSave = this.lastSave;
        if (lastSave === null) {
            return false;
        }

        const current = currentTime.getValue();
        if (current === null) {
            return false;
        }

        if (lastSave + 15 > current) {
            return false;
        }

        return this.getValue() !== this.getValueOriginal();
    }
}

const buildValue = (value: number): string => {
    return $appState.configState.precision.newFromOld(value).value;
};

const getSportsGrades = async (sportId: string): Promise<Array<GradesCustomType>> => {
    const data = await $appState.sdkStaff.trading.getSportsGrades(sportId);

    const sport = await $appState.sdkStaff.getSport(sportId);

    return data.map((grade): GradesCustomType => {
        const defaultValue = grade.id === sport.defaultGrade?.id;

        const defaultLimits = {
            liabilityLimit: buildValue(grade.defaultLimits.liabilityLimit),
            maxBet: buildValue(grade.defaultLimits.maxBet),
            maxMultipleBet: buildValue(grade.defaultLimits.maxMultipleBet),
            maxWin: buildValue(grade.defaultLimits.maxWin),
        };

        const marketLimits: Record<string, DefaultLimitsType> = {};

        for (const [marketLimitsKey, marketLimitsValue] of Object.entries(grade.marketLimits)) {
            marketLimits[marketLimitsKey] = {
                liabilityLimit: buildValue(marketLimitsValue.liabilityLimit),
                maxBet: buildValue(marketLimitsValue.maxBet),
                maxMultipleBet: buildValue(marketLimitsValue.maxMultipleBet),
                maxWin: buildValue(marketLimitsValue.maxWin),
            };
        }

        return {
            ...grade,
            default: defaultValue,
            defaultLimits,
            marketLimits,
        };
    });
};

export class SportGradesState {
    private readonly gradesResource: Resource<Array<GradesCustomType>>;
    private readonly gradesMarketTemplates: Resource<Array<MarketTemplateType>>;

    @observable private selectId: number | null;
    @observable private newName: string | undefined;
    public readonly valuesInput: MobxMapAutoNew<
        number,
        MobxMapAutoNew<string, MobxMapAutoNew<LimitFieldNameType, DefaultLimitsInputState>>
    >;

    public constructor(private readonly sportId: string) {
        this.gradesResource = new Resource(async () => {
            const response = await getSportsGrades(sportId);

            response.sort((a, b): number => {
                const aName = a.name.toLowerCase();
                const bName = b.name.toLowerCase();

                if (aName > bName) {
                    return 1;
                }

                if (aName < bName) {
                    return -1;
                }

                return 0;
            });

            return response;
        });

        this.gradesMarketTemplates = new Resource(async () => {
            const response = await $appState.sdkStaff.getMarketTemplates(sportId);
            return response.results;
        });

        this.selectId = null;
        this.newName = undefined;
        this.valuesInput = new MobxMapAutoNew(
            (gradeId: number) =>
                new MobxMapAutoNew(
                    (templateId: string) =>
                        new MobxMapAutoNew(
                            (field) => new DefaultLimitsInputState(this.getGradeById, gradeId, templateId, field)
                        )
                )
        );
        makeAutoObservable(this);
    }

    public get grades(): Array<GradeType> {
        return this.gradesResource.getReady() ?? [];
    }

    public getTemplateKey = (template: MarketTemplateType): string => {
        if (template.id.includes('custom')) {
            return template.id;
        }

        return template.matchingMarkets ?? template.id;
    };

    public get marketTemplates(): Array<MarketTemplateType> {
        return this.gradesMarketTemplates.getReady() ?? [];
    }

    public get(gradeId: number, templateId: string, fieldLimitId: LimitFieldNameType): DefaultLimitsInputState {
        return this.valuesInput.get(gradeId).get(templateId).get(fieldLimitId);
    }

    public valueGet(gradeId: number, templateId: string, fieldLimitId: LimitFieldNameType): string {
        return this.get(gradeId, templateId, fieldLimitId).getValue();
    }

    private findMatchingMarkets(gradeMarketTamplateId: string): string | null {
        return this.marketTemplates.find((template) => template.id === gradeMarketTamplateId)?.matchingMarkets ?? null;
    }

    private getGradeById = (gradeId: number | null): GradeType | null => {
        for (const grade of this.grades) {
            if (grade.id === gradeId) {
                return grade;
            }
        }

        return null;
    };

    @computed public get getSelectGrade(): GradeType | null {
        return this.getGradeById(this.selectId) ?? this.grades[0] ?? null;
    }

    @computed public get getName(): string {
        const { newName } = this;

        if (newName !== undefined) {
            return newName;
        }

        const grade = this.getSelectGrade;

        if (grade !== null) {
            return grade.displayName === null || grade.displayName === undefined ? grade.name : grade.displayName;
        }

        return '';
    }

    public onNameChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
        this.newName = e.target.value;
    };

    public onNameBlur = async (grade: GradesCustomType): Promise<void> => {
        const newName = this.newName ?? '';

        if (newName !== '' && newName !== grade.displayName) {
            await this.onUpdateGradeRequest(grade.id, {
                name: grade.displayName === null || grade.displayName === undefined ? newName : grade.name,
                displayName: grade.displayName === null || grade.displayName === undefined ? undefined : newName,
            });
        }
    };

    @action public toggleGrade = (id: string): void => {
        const selectId = parseInt(id, 10);

        if (isNaN(selectId)) {
            console.error('nieprawidłowy ID', id);
            return;
        }

        this.newName = undefined;
        this.selectId = selectId;
    };

    public async onCreateGrade(
        grade: GradesCustomType,
        e: React.MouseEvent<HTMLAnchorElement, MouseEvent>
    ): Promise<void> {
        e.preventDefault();

        // eslint-disable-next-line no-alert
        const name = window.prompt('Grade name');

        if (name !== null) {
            await this.onCreateGradeRequest({
                name,
                defaultLimits: grade.defaultLimits,
            });
        }
    }

    private async getLimitsForUpdate(gradeId: number): Promise<Record<string, MarketLimitsUpdateType>> {
        const result: Record<string, MarketLimitsUpdateType> = {};

        for (const marketTemplate of this.marketTemplates) {
            const templateId = this.getTemplateKey(marketTemplate);

            result[templateId] = {
                betLimits: {
                    maxWin: this.buildOldValue(this.valueGet(gradeId, templateId, 'maxWin')),
                    maxBet: this.buildOldValue(this.valueGet(gradeId, templateId, 'maxBet')),
                    maxMultipleBet: this.buildOldValue(this.valueGet(gradeId, templateId, 'maxMultipleBet')),
                    liabilityLimit: this.buildOldValue(this.valueGet(gradeId, templateId, 'liabilityLimit')),
                },
                matchingMarkets: this.findMatchingMarkets(templateId),
            };
        }

        return result;
    }

    public async blurValue(grade: GradesCustomType, templateId: string): Promise<void> {
        const marketLimits = await this.getLimitsForUpdate(grade.id);
        // eslint-disable-next-line no-unused-expressions
        grade.marketLimits;
        await this.onUpdateGradeRequest(grade.id, {
            name: grade.name,
            marketLimits,
        });
        this.get(grade.id, templateId, 'maxWin').setLastSave();
        this.get(grade.id, templateId, 'maxBet').setLastSave();
        this.get(grade.id, templateId, 'maxMultipleBet').setLastSave();
        this.get(grade.id, templateId, 'liabilityLimit').setLastSave();

        await this.refreshGrades();
    }

    private async refreshGrades(): Promise<void> {
        await this.gradesResource.refresh();
        await timeout(10000);
        await this.gradesResource.refresh();
        await timeout(10000);
        await this.gradesResource.refresh();
    }

    private buildOldValue(value: string): number {
        return $appState.configState.precision.valueOldFormat(new Amount(value));
    }

    public async onCreateGradeRequest(data: { name: string; defaultLimits: DefaultLimitsType }): Promise<void> {
        for (const grade of this.gradesResource.getReady() ?? []) {
            if (grade.name === data.name) {
                // eslint-disable-next-line no-alert
                alert(`this name "${data.name}" is already taking another grade`);
                return;
            }
        }

        await $appState.sdkStaff.sportGradeAdd({
            sport_id: this.sportId,
            requestBody: {
                name: data.name,
                defaultLimits: {
                    liabilityLimit: this.buildOldValue(data.defaultLimits.liabilityLimit),
                    maxBet: this.buildOldValue(data.defaultLimits.maxBet),
                    maxMultipleBet: this.buildOldValue(data.defaultLimits.maxMultipleBet),
                    maxWin: this.buildOldValue(data.defaultLimits.maxWin),
                },
            },
        });

        await this.refreshGrades();
    }

    public async onUpdateGradeRequest(
        gradeId: number,
        data: { name: string; displayName?: string; marketLimits?: Record<string, MarketLimitsUpdateType> }
    ): Promise<void> {
        await $appState.sdkStaff.trading.updateSportGrade({
            sport_id: this.sportId,
            grade_id: gradeId.toString(),
            requestBody: { marketLimits: data.marketLimits ?? {}, name: data.name, displayName: data.displayName },
        });
    }
}

export class MobxMapSportGradesState {
    private data: MobxMapAutoNew<string, SportGradesState>;

    public constructor() {
        this.data = new MobxMapAutoNew((sportId: string) => new SportGradesState(sportId));
    }

    public get(sportId: string): SportGradesState {
        return this.data.get(sportId);
    }
}
